import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface BookingSlot {
    type: "book" | "free";
    start: string;
    end: string;
    service?: string;
    user?: string;
    price?: number;
    duration?: number;
    confirmed: boolean;
    mia_candidate: boolean;
}

interface FreeSlot {
    start: string; // Start time of the free slot in "HH:mm" format
    end: string; // End time of the free slot in "HH:mm" format
    working?: boolean;
}

export interface FreeDay {
    date: string; // The date of the day (e.g., "YYYY-MM-DD")
    freeSlots: FreeSlot[]; // Array of free slots available on this date
}

interface ScheduleDay {
    slots: BookingSlot[];
    workingHours: [start: string, end: string];
}

export default class Schedule {
    /**
     * Retrieves the schedule for a specific day for a given brand and location.
     * @param brand - The brand ID.
     * @param location - The location ID.
     * @param year - The year of the desired date.
     * @param month - The month of the desired date (1-indexed).
     * @param day - The day of the month.
     * @param userToken - The user token for authentication.
     * @returns An array of booking and free slots for the day.
     */
    public static readonly scheduleDay = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        day: number
    ): Promise<ScheduleDay | undefined> => {
        try {
            const { data } = await axios.get(
                `${environment.api_url}/schedule/${brand}/location/${location}/${year}/${month}/${day}`,
                { headers: { "User-Token": uuid } }
            );
            return data;
        } catch (error) {
            console.error("Error fetching schedule:", error);
            return undefined;
        }
    };

    public static readonly getFreeSlots = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        duration: number,
        day?: number
    ): Promise<FreeDay[] | undefined> => {
        try {
            const endpoint = `${
                environment.api_url
            }/${brand}/location/${location}/free/${year}/${month}${
                day !== undefined ? `/${day}` : ""
            }`;

            const { data } = await axios.get(endpoint, {
                headers: {
                    "User-Token": uuid,
                },
                params: { duration }, // Sending duration as a query parameter
            });

            return data;
        } catch (error) {
            console.error("Error fetching free slots:", error);
            return undefined;
        }
    };
}
